import React, {useState, useEffect, useCallback, useMemo} from 'react';
import { useSearchParams } from 'react-router-dom';
import './App.css';

// Components
import StateWide from './components/StateWide';
import DistrictWise from './components/DistrictWise';
import LiveUpdates from './components/LiveUpdates';
import LocalBodyWise from './components/LocalBodyWise';
import PartyWiseResults from './components/PartyWiseResults';
import HeaderWithClose from './components/HeaderWithClose';
import Trends from "./components/Trends";
import ScrollableTabs from "./components/ScrollableTabs";
import LoadingSpinner from "./components/LoadingSpinner";

// Constants
const MOBILE_BREAKPOINT = 1024;
const POPUP_VIEWS = {
  PARTIES: 'parties',
  STATEWIDE: 'statewide',
  DISTRICTS: 'districts',
  LOCALBODIES: 'localbodies',
};

const App = () => {
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('state');
  const [isMobile, setIsMobile] = useState(false);
  const [activeDesktopTab, setActiveDesktopTab] = useState('localBody');
  const [showComponent, setShowComponent] = useState(null);
  const [searchParams] = useSearchParams();
  const [showMore, setShowMore] = useState(false);

  // Effects
  useEffect(() => {
    const component = searchParams.get('show');
    if (Object.values(POPUP_VIEWS).includes(component)) {
      setShowComponent(component);
    }
  }, [searchParams]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleMoreClick = () => {
    const newState = !showMore;
    setShowMore(newState);
  };

  // Handlers
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);
  const handleDesktopTabChange = (tab) => setActiveDesktopTab(tab);

  const localBodyWiseInstance = useMemo(() => <LocalBodyWise />, []);
  const districtWiseInstance = useMemo(() => <DistrictWise />, []);
  const stateWideInstance = useMemo(() => <StateWide />, []);
  const liveUpdatesInstance = useMemo(() => <LiveUpdates />, []);
  const trendsInstance = useMemo(() => <Trends />, []);

  // Render Methods
  const renderMobileView = useCallback(() => (
    <div className="mobile-tabs">
      <ScrollableTabs
        tabs={[
          { id: 'state', label: 'STATEWIDE' },
          { id: 'district', label: 'DISTRICT-WISE' },
          { id: 'localBody', label: 'LOCALBODY-WISE' },
          // { id: 'updates', label: 'LIVE UPDATES' },
          { id: 'trends', label: 'KEY CANDIDATES' },
        ]}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />
      <div className="app-tab-content">
        {activeTab === 'state' && stateWideInstance}
        {activeTab === 'district' && districtWiseInstance}
        {activeTab === 'localBody' && localBodyWiseInstance}
        {/*{activeTab === 'updates' && liveUpdatesInstance}*/}
        {activeTab === 'trends' && trendsInstance}
      </div>
    </div>
  ), [
    activeTab,
    handleTabChange,
    stateWideInstance,
    districtWiseInstance,
    localBodyWiseInstance,
    // liveUpdatesInstance,
    trendsInstance
  ]);

  const renderDesktopView = () => (
    <>
      <div className="desktop-view">
        <div className="panel election-results-container">
          <h2>STATEWIDE RESULTS</h2>
          {stateWideInstance}
        </div>
        <div className="panel election-results-container">
          <h2>DISTRICT-WISE RESULTS</h2>
          {districtWiseInstance}
        </div>
        <div className="panel combined-panel">
          <div className="desktop-tabs">
            {[
              { id: 'localBody', label: 'LOCAL BODY WISE' },
              // { id: 'updates', label: 'LIVE UPDATES' },
              { id: 'trends', label: 'KEY CANDIDATES' },
            ].map(({ id, label }) => (
              <button
                key={id}
                aria-current={activeTab === id ? 'page' : undefined}
                className={`desktop-tab ${activeDesktopTab === id ? 'active' : ''}`}
                onClick={() => handleDesktopTabChange(id)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="desktop-tab-content">
            {activeDesktopTab === 'localBody' ? localBodyWiseInstance : activeDesktopTab === 'updates' ? liveUpdatesInstance : trendsInstance}
          </div>
        </div>
      </div>
      <p style={{float: 'right', marginTop: '10px', color: '#787878', fontSize: '12px', fontFamily: 'Noto Sans Malayalam, sans-serif'}}>* നിലവിലെ ഭരണം</p>
    </>
  );

  const renderPopupView = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const list = searchParams.get('list');

    return (
      <div className="App" style={{padding: '0 1%'}}>
        <div className="container-pop">
          <HeaderWithClose/>
          {showComponent === POPUP_VIEWS.PARTIES && <PartyWiseResults list={list} />}
          {showComponent === POPUP_VIEWS.STATEWIDE && stateWideInstance}
          {showComponent === POPUP_VIEWS.DISTRICTS && districtWiseInstance}
          {showComponent === POPUP_VIEWS.LOCALBODIES && localBodyWiseInstance}
          <p className="copyright">© Copyright Mathrubhumi 2025. All rights reserved.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    // This simulates loading time - remove this in production
    // and replace with your actual loading logic
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000); // Adjust time as needed

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <LoadingSpinner color="#403E3E" showText={true} />;
  }

  // Main Render
  if (showComponent) {
    return renderPopupView();
  }

  return (
    <div className="app">
      {isMobile ?
        (
          <>
            {renderMobileView()}

            <button
              onClick={handleMoreClick}
              className="more-button"
              style={{
                width: 'max-content',
                padding: '10px',
                background: '#f0f0f0',
                borderRadius: '10px',
                border: '1px solid #D3D3D3',
                margin: '20px auto',
                cursor: 'pointer',
                zIndex: 1000,
                position: 'relative',
                display: 'flex',
              }}
            >
              {showMore ? 'SHOW LESS' : 'SHOW MORE'}
            </button>

            {showMore && (
              <div className="more-content" style={{
                marginTop: '15px',
                position: 'relative',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                backgroundColor: '#FFF',
              }}>
                {districtWiseInstance}
                {localBodyWiseInstance}
                {/*{liveUpdatesInstance}*/}
                {trendsInstance}
              </div>
            )}
          </>
        )
        :
        renderDesktopView()
      }
    </div>
  );
};

export default App;